import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Lock, MessageCircle, LayoutDashboard, Layers, UserPlus, Eye, Maximize2, HelpCircle } from "lucide-react";
import { useLocation } from "wouter";

interface TrackerHeaderProps {
  isEventCompleted?: boolean;
  onAddParticipant: () => void;
  showMessageNotification?: boolean;
  onMessageNotificationClick?: () => void;
  hasNewMessages?: boolean;
  showKanbanControls?: boolean;
  isKanbanView?: boolean;
  onToggleKanbanView?: () => void;
  showParticipantsInKanban?: boolean;
  onToggleParticipants?: () => void;
  showConsiderationInKanban?: boolean;
  onToggleConsideration?: () => void;
  jamNightId?: number;
  onFocusedViewClick?: () => void;
}

export function TrackerHeader({
  isEventCompleted = false,
  onAddParticipant,
  showMessageNotification = false,
  onMessageNotificationClick,
  hasNewMessages = false,
  showKanbanControls = false,
  isKanbanView = false,
  onToggleKanbanView,
  showParticipantsInKanban = false,
  onToggleParticipants,
  showConsiderationInKanban = true,
  onToggleConsideration,
  jamNightId,
  onFocusedViewClick,
}: TrackerHeaderProps) {
  const [, setLocation] = useLocation();

  return (
    <>
      {/* Header */}
      <header className={`sticky-header-ios z-50 backdrop-blur-xl border-b border-white/20 transition-all duration-200 ${
        hasNewMessages ? 'bg-red-100/40' : 'bg-white/30'
      }`}
        style={{
          boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
        }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLocation("/event-management")}
                className="flex items-center gap-2 px-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              {/* Focused view button - aligned left next to back button */}
              {onFocusedViewClick && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onFocusedViewClick}
                  className="flex items-center gap-2 bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 hover:text-white border border-blue-500/30 shadow-lg transition-all duration-300"
                  style={{
                    boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                  title="Focused View"
                >
                  <Eye className="h-4 w-4" />
                  <span className="hidden sm:inline">Focus</span>
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {showMessageNotification && (
                <MessageNotificationIcon 
                  hasNewMessages={hasNewMessages}
                  onClick={onMessageNotificationClick}
                />
              )}
              {/* View controls - only render when on live tab */}
              {showKanbanControls && (
                <>
                  {isKanbanView && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onToggleConsideration}
                        className="hidden lg:flex items-center gap-2"
                      >
                        <HelpCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">{showConsiderationInKanban ? 'Hide' : 'Show'} Consideration</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onToggleParticipants}
                        className="hidden lg:flex items-center gap-2"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span className="hidden xl:inline">{showParticipantsInKanban ? 'Hide' : 'Show'} Participants</span>
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onToggleKanbanView}
                    className="hidden lg:flex items-center gap-2 bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 hover:text-white border border-blue-500/30 shadow-lg transition-all duration-300"
                    style={{
                      boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    {isKanbanView ? (
                      <>
                        <Layers className="h-4 w-4" />
                        <span className="hidden xl:inline">Standard View</span>
                      </>
                    ) : (
                      <>
                        <LayoutDashboard className="h-4 w-4" />
                        <span className="hidden xl:inline">Column View</span>
                      </>
                    )}
                  </Button>
                </>
              )}
              <Button
                onClick={onAddParticipant}
                variant="outline"
                size="sm"
                disabled={isEventCompleted}
                className="flex items-center gap-2 bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 hover:text-white border border-green-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <span className="mr-2">+</span>Add Participant
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Event Completed Banner */}
      {isEventCompleted && (
        <div className="bg-gray-500/30 backdrop-blur-sm border-b border-gray-300/50 py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <Lock className="h-5 w-5" />
              <span className="font-medium">This event has been marked as completed and is now read-only</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MessageNotificationIcon({ 
  hasNewMessages, 
  onClick 
}: { 
  hasNewMessages: boolean;
  onClick?: () => void;
}) {
  if (!hasNewMessages) {
    return null;
  }

  return (
    <div className="relative cursor-pointer" onClick={onClick}>
      <MessageCircle className="h-5 w-5 text-blue-600" />
      <Badge
        variant="destructive"
        className="absolute -top-2 -right-2 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs"
      >
        !
      </Badge>
    </div>
  );
}